import { isNumber } from 'lodash';

import {
  CustomMetric,
  HistogramSeriesViewRequest,
  View,
  XAxis,
} from '@neptune/shared/core-apis-leaderboard-domain';
import { EntityModelConverter } from '@neptune/shared/entity-domain';
import { RunsLineage } from '@neptune/shared/leaderboard-domain';
import { AxisScale, XAxisMode } from '@neptune/shared/widgets-domain';

import { HistogramSource } from './histogram-widget';

export type CreateHistogramSeriesRequestParams = {
  maxNumberOfBins: number;
  xAxisScale: AxisScale;
  xAxisMode: XAxisMode;
  sources: HistogramSource[];
  xAxisBoundaries: { min?: number; max?: number };
  maxUniqueX?: number;
  lineage: RunsLineage;
};

export function createHistogramSeriesRequest({
  maxNumberOfBins,
  xAxisBoundaries,
  xAxisScale,
  xAxisMode,
  maxUniqueX = 300,
  sources,
  lineage,
}: CreateHistogramSeriesRequestParams): HistogramSeriesViewRequest {
  const { min, max } = xAxisBoundaries;

  const view: View = {
    from: min,
    to: isNumber(max) && max === min ? max + 1 : max,
    maxUniqueX,
    plotMode: { line: {} },
    xscale: {
      ...(xAxisScale ? { [xAxisScale === 'logarithmic' ? 'log' : 'linear']: {} } : {}),
    },
    yscale: { linear: {} },
  };

  return {
    maxNumberOfBins,
    view,
    metrics: createMetricsFromSources(sources, lineage),
    xaxis: resolveXAxis(xAxisMode),
  };
}

function resolveXAxis(xAxisMode: XAxisMode): XAxis {
  if (xAxisMode === 'step') {
    return {
      steps: {},
    };
  } else if (xAxisMode === 'relativeTime') {
    return {
      relativeMillis: {},
    };
  }

  return {
    epochMillis: {},
  };
}

function createMetricsFromSources(
  sources: HistogramSource[],
  lineage: RunsLineage,
): CustomMetric[] {
  return sources.map((entity) => {
    const attributes = entity.attributes.map((attr) => attr.name);
    return {
      attributes,
      holder: {
        identifier: entity.entityId,
        type: EntityModelConverter.entityTypeToHolderType(entity.entityType),
      },
      customId: entity.entityId,
      lineage,
      lineageEntityType: entity.lineageEntityType,
    };
  });
}
